import { useState, useEffect, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import SkeletonLoadWrapper from 'pricing-pages-ui-components/loading/SkeletonLoadWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
// Defined at module scope so its component identity is stable across renders.
// Re-creating it inside the hook would unmount/remount the children subtree
// every render, causing flicker and dropped focus.
const TranslationSkeleton = ({
  isLoading,
  children,
  widths,
  heights
}) => /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
  isLoading: isLoading,
  widths: widths,
  heights: heights,
  children: children
});
export const useScopedTranslations = (translations, i18nLoader, shouldLoad = true) => {
  const [isReady, setIsReady] = useState(false);
  const [isError, setIsError] = useState(false);
  const hasRegistered = useRef(false);
  useEffect(() => {
    if (!shouldLoad || !i18nLoader || hasRegistered.current) return;
    hasRegistered.current = true;
    let cancelled = false;
    i18nLoader.register(translations).then(() => {
      if (!cancelled) setIsReady(true);
    }).catch(e => {
      if (!cancelled) {
        setIsError(true);
        Raven.captureException(e, {
          fingerprint: ['Failed to register translations']
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [i18nLoader, shouldLoad, translations]);

  // On error, render children with a static fallback so the page is not blank.
  const isLoading = !(isReady || isError);

  // useMemo so the bound component reference only changes when isLoading flips,
  // not on every render of the consumer.
  const Skeleton = useMemo(() => function BoundSkeleton(props) {
    return /*#__PURE__*/_jsx(TranslationSkeleton, Object.assign({
      isLoading: isLoading
    }, props));
  }, [isLoading]);
  return {
    isReady,
    isError,
    Skeleton
  };
};